import { useEffect, useState, useRef } from "react";
import { Box, Typography, Grid, makeStyles } from "@material-ui/core";

import { COLORS } from "../../../../theme/colors";
import { NoDataCard } from "../../../../components/parts";
import { BuildingsMap } from "../../components/BuildingsMap";
import { BuildingsTable } from "../../components/BuildingsTable";
import { ShaDowBox } from "../../../../components/boxes/ShadowBox";
import { HorizontalTabs } from "../../../../components/horizontalTabs";
import { CustomPieChart, LabelBarChart } from "../../../../components/charts";
import {
  currencyFormatter,
  getData,
  isObjectEmpty,
  nKilowattFormatter,
  onlyUniqueValuesOfKeyInArray,
  sumFromListOfObjects,
} from "../../../../services";
import { QuantityChart } from "../../components/QuantityChart";
import { DEFAULT_HEADERS } from "../../constants";
import { DEFAULT_DRAFT_URL } from "../../../aroundtown/PortfolioRegistrationPage";

const BAR_COLORS = [
  COLORS.shadowBlue,
  COLORS.olivine,
  COLORS.majorelleBlue,
  COLORS.blue,
  COLORS.verdigris,
  COLORS.pearlyPurple,
  "#5D5D5D",
  "#E8A3D9",
];
export const MAX_BUBBLES_NUMBER = 8;
const OTHER_COLOR = BAR_COLORS[MAX_BUBBLES_NUMBER - 1];

const useStyles = makeStyles(() => ({
  segmentGrid: {
    marginRight: 30,
  },
  segmentBox: {
    width: 23,
    height: 6,
    marginRight: 6,
    borderRadius: 8,
  },
  chartBox: {
    height: 270,
    borderRadius: 8,
    background: COLORS.darks[0][3],
    marginTop: 20,
  },
  tabsContainer: {
    borderBottom: `solid 4px ${COLORS.americanSilver}`,
    justifyContent: "space-between",
  },
}));

export const ReviewCalculationStep = ({
  rows,
  setRows,
  headers = DEFAULT_HEADERS,
  draftUrl = DEFAULT_DRAFT_URL,
}) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [electricityCostsChartData, setElectricityCostsChartData] = useState([]);
  const [electricityConsumptionChartData, setElectricityConsumptionChartData] = useState([]);
  const [totalAreaChartData, setTotalAreaChartData] = useState([]);
  const [roofAreaChartData, setRoofAreaChartData] = useState([]);
  const [quantityChartData, setQuantityChartData] = useState([]);
  const validAddressBuildings = rows.filter((r) => r.is_valid_address);
  const invalidAddressesCount = rows.length - validAddressBuildings.length;
  const [tabNames, setTabNames] = useState([]);
  const bubbleChartContainerRef = useRef();
  const [bars, setBars] = useState([]);
  const [states, setStates] = useState([]);
  const [bubbleColors, setBubbleColors] = useState({});

  const hasElectricityData =
    electricityCostsChartData.length || electricityConsumptionChartData.length;

  useEffect(() => {
    getData(`onboarding/n/analytics/`, (data) => {
      const electricity_costs_2021 = {};
      const electricity_costs_2022 = {};
      const electricity_costs_2023 = {};

      const electricity_consumption_2021 = {};
      const electricity_consumption_2022 = {};
      const electricity_consumption_2023 = {};

      const totalAreaChartData = [];
      const roofAreaChartData = [];

      const totalCount = sumFromListOfObjects(data, "count");
      data.forEach((item) => (item["percent"] = (item.count * 100) / totalCount));

      const mainStates = data.slice(0, MAX_BUBBLES_NUMBER - 1);
      const otherStates = data.slice(MAX_BUBBLES_NUMBER - 1);
      const temporaryBars = [];
      const temporaryStates = [];
      const temporaryBubbleColors = {};
      const othersStatesBubleData = {};

      mainStates.forEach((item, i) => {
        const name = item.state?.toLowerCase().replace(" ", "_");
        temporaryStates.push(name);
        temporaryBars.push({
          key: name,
          color: BAR_COLORS[i],
        });
        temporaryBubbleColors[name] = BAR_COLORS[i];
        if (item.electricity_costs._2021 !== null)
          electricity_costs_2021[name] = item.electricity_costs._2021;
        if (item.electricity_costs._2022 !== null)
          electricity_costs_2022[name] = item.electricity_costs._2022;
        if (item.electricity_costs._2023 !== null)
          electricity_costs_2023[name] = item.electricity_costs._2023;

        if (item.electricity_consumption._2021 !== null)
          electricity_consumption_2021[name] = item.electricity_consumption._2021;
        if (item.electricity_consumption._2022 !== null)
          electricity_consumption_2022[name] = item.electricity_consumption._2022;
        if (item.electricity_consumption._2023 !== null)
          electricity_consumption_2023[name] = item.electricity_consumption._2023;

        const totalArea = {
          name: item.state,
          value: item.sum_total_square,
          color: BAR_COLORS[i],
          unit: "unit_system",
        };
        totalAreaChartData.push(totalArea);

        const roofArea = {
          name: item.state,
          value: item.sum_roof_square,
          color: BAR_COLORS[i],
          unit: "unit_system",
        };
        roofAreaChartData.push(roofArea);
      });

      if (otherStates.length) {
        temporaryBars.push({
          key: "others",
          color: OTHER_COLOR,
        });
        temporaryStates.push("others");
        temporaryBubbleColors["others"] = OTHER_COLOR;
        othersStatesBubleData.state = "others";
        othersStatesBubleData.count = 0;
        othersStatesBubleData.percent = 0;

        electricity_costs_2021.others = 0;
        electricity_costs_2022.others = 0;
        electricity_costs_2023.others = 0;

        electricity_consumption_2021.others = 0;
        electricity_consumption_2022.others = 0;
        electricity_consumption_2023.others = 0;

        const totalArea = {
          name: "Others",
          value: 0,
          color: OTHER_COLOR,
          unit: "unit_system",
        };

        const roofArea = {
          name: "Others",
          value: 0,
          color: OTHER_COLOR,
          unit: "unit_system",
        };

        otherStates.forEach((item) => {
          if (item.electricity_costs._2021 !== null)
            electricity_costs_2021["others"] += item.electricity_costs._2021;
          if (item.electricity_costs._2022 !== null)
            electricity_costs_2022["others"] += item.electricity_costs._2022;
          if (item.electricity_costs._2023 !== null)
            electricity_costs_2023["others"] += item.electricity_costs._2023;

          if (item.electricity_consumption._2021 !== null)
            electricity_consumption_2021["others"] += item.electricity_consumption._2021;
          if (item.electricity_consumption._2022 !== null)
            electricity_consumption_2022["others"] += item.electricity_consumption._2022;
          if (item.electricity_consumption._2023 !== null)
            electricity_consumption_2023["others"] += item.electricity_consumption._2023;

          if (item.sum_total_square !== null) totalArea.value += item.sum_total_square;
          if (item.sum_roof_square !== null) roofArea.value += item.sum_roof_square;

          othersStatesBubleData.count += item.count;
          othersStatesBubleData.percent += item.percent;
        });

        totalAreaChartData.push(totalArea);
        roofAreaChartData.push(roofArea);
      }

      setBars(temporaryBars);
      setStates(temporaryStates);
      setBubbleColors(temporaryBubbleColors);
      let bubbleData = [...mainStates];
      if (!isObjectEmpty(othersStatesBubleData)) bubbleData.push(othersStatesBubleData);
      setQuantityChartData(bubbleData);

      if (!onlyUniqueValuesOfKeyInArray(totalAreaChartData, "value", null)) {
        setTotalAreaChartData(totalAreaChartData);
      }

      if (!onlyUniqueValuesOfKeyInArray(roofAreaChartData, "value", null)) {
        setRoofAreaChartData(roofAreaChartData);
      }

      const electricityCosts = [];

      if (!isObjectEmpty(electricity_costs_2021)) {
        electricity_costs_2021["x"] = "2021";
        electricityCosts.push(electricity_costs_2021);
      }

      if (!isObjectEmpty(electricity_costs_2022)) {
        electricity_costs_2022["x"] = "2022";
        electricityCosts.push(electricity_costs_2022);
      }

      if (!isObjectEmpty(electricity_costs_2023)) {
        electricity_costs_2023["x"] = "2023";
        electricityCosts.push(electricity_costs_2023);
      }

      if (electricityCosts.length) {
        setElectricityCostsChartData([
          electricity_costs_2021,
          electricity_costs_2022,
          electricity_costs_2023,
        ]);
      }

      const electricityConsumption = [];
      if (!isObjectEmpty(electricity_consumption_2021)) {
        electricity_consumption_2021["x"] = "2021";
        electricity_consumption_2021["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2021);
      }

      if (!isObjectEmpty(electricity_consumption_2022)) {
        electricity_consumption_2022["x"] = "2022";
        electricity_consumption_2022["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2022);
      }

      if (!isObjectEmpty(electricity_consumption_2023)) {
        electricity_consumption_2023["x"] = "2023";
        electricity_consumption_2023["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2023);
      }

      if (electricityConsumption.length) {
        setElectricityConsumptionChartData([
          electricity_consumption_2021,
          electricity_consumption_2022,
          electricity_consumption_2023,
        ]);
      }

      if (electricityCosts.length || electricityConsumption.length) {
        setTabNames(["Quantity", "Total building Area", "Total roof Area"]);
      }
    });
  }, []);

  return (
    <>
      <ShaDowBox border style={{ marginBottom: 30 }}>
        <Box p={4} bgcolor={COLORS.darks[0][6]} mb={0.5}>
          <Typography variant="h2">
            {rows.length - invalidAddressesCount} buildings&apos;
            {validAddressBuildings.length < rows.length && (
              <span style={{ color: COLORS.red }}>*</span>
            )}{" "}
            analytics
          </Typography>
        </Box>

        <Box position="relative">
          <Box
            position="absolute"
            py={3}
            px={6}
            zIndex={2}
            width="45%"
            height="100%"
            style={{ background: "linear-gradient(to right, white 50%, transparent)" }}
            boxSizing="border-box"
          >
            <Typography variant="h4">
              The <span style={{ color: COLORS.purple, fontWeight: "bold" }}>PRaaS System</span> has
              calculated the
            </Typography>
            <Typography variant="h2">Estimated Power Revenues</Typography>
            <Typography variant="h4">based on your data</Typography>
          </Box>
          <BuildingsMap buildings={validAddressBuildings} height={335} />
        </Box>

        <Box px={4} pb={0} pt={6}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h3">
                {rows.length - invalidAddressesCount} Buildings analysis by states
              </Typography>
            </Grid>
            {states.length ? (
              <Grid container style={{ width: "auto" }}>
                {states.map((state, i) => {
                  return (
                    <Grid
                      key={i}
                      container
                      className={classes.segmentGrid}
                      style={{ width: "auto" }}
                      alignItems="center"
                    >
                      <Box
                        className={classes.segmentBox}
                        style={{ background: BAR_COLORS[i] }}
                      ></Box>
                      <Typography style={{ textTransform: "capitalize" }}>{state}</Typography>
                    </Grid>
                  );
                })}
              </Grid>
            ) : null}
          </Grid>
        </Box>

        <Box p={4}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              {hasElectricityData ? (
                <HorizontalTabs
                  tab={tab}
                  setTab={setTab}
                  tabNames={tabNames}
                  tabStyle={{
                    fontSize: 18,
                    marginRight: -10,
                  }}
                  flexContainer={classes.tabsContainer}
                />
              ) : (
                <Typography
                  variant="h4"
                  style={{ textAlign: "center", paddingTop: 15, paddingBottom: 12 }}
                >
                  Quantity
                </Typography>
              )}
              <Box
                ref={bubbleChartContainerRef}
                display="flex"
                justifyContent="center"
                alignItems="center"
                className={classes.chartBox}
                style={{ marginTop: 8 }}
              >
                {tab === 0 ? (
                  quantityChartData.length ? (
                    <QuantityChart
                      data={quantityChartData}
                      containerRef={bubbleChartContainerRef}
                      bubbleKey="state"
                      bubbleColors={bubbleColors}
                    />
                  ) : (
                    <NoDataCard withShaDowBox={false} gridStyle={{ height: 160 }} />
                  )
                ) : tab === 1 ? (
                  totalAreaChartData.length ? (
                    <CustomPieChart name="area" data={totalAreaChartData} />
                  ) : (
                    <NoDataCard withShaDowBox={false} type="pie" />
                  )
                ) : roofAreaChartData.length ? (
                  <CustomPieChart name="area" data={roofAreaChartData} />
                ) : (
                  <NoDataCard withShaDowBox={false} type="pie" />
                )}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h4" style={{ textAlign: "center", paddingTop: 15 }}>
                {hasElectricityData ? "Total electricity costs" : "Total building Area"}
              </Typography>
              <Box className={classes.chartBox}>
                {!electricityCostsChartData.length && !electricityConsumptionChartData.length ? (
                  <CustomPieChart name="area" data={totalAreaChartData} />
                ) : electricityCostsChartData.length ? (
                  <LabelBarChart
                    top={50}
                    left={5}
                    right={35}
                    height={270}
                    chartData={electricityCostsChartData}
                    bars={bars}
                    barSize={50}
                    yTickFormatter={currencyFormatter}
                  />
                ) : (
                  <NoDataCard withShaDowBox={false} gridStyle={{ height: 160 }} />
                )}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h4" style={{ textAlign: "center", paddingTop: 15 }}>
                {hasElectricityData ? "Est. Electricity consumption" : "Total roof Area"}
              </Typography>
              <Box className={classes.chartBox}>
                {!electricityCostsChartData.length && !electricityConsumptionChartData.length ? (
                  <CustomPieChart name="area" data={roofAreaChartData} />
                ) : electricityConsumptionChartData.length ? (
                  <LabelBarChart
                    top={50}
                    left={5}
                    right={35}
                    height={270}
                    chartData={electricityConsumptionChartData}
                    bars={bars}
                    barSize={50}
                    yTickFormatter={nKilowattFormatter}
                  />
                ) : (
                  <NoDataCard withShaDowBox={false} gridStyle={{ height: 160 }} />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {invalidAddressesCount > 0 ? (
          <Box p={4}>
            <Typography style={{ color: COLORS.red }}>
              * {rows.length - validAddressBuildings.length} buildings could not be located by
              Google, due to incorrect addresses and are excluded from analytics.
            </Typography>
          </Box>
        ) : null}
      </ShaDowBox>

      <BuildingsTable
        rows={rows}
        setRows={setRows}
        disableTable
        headers={headers}
        draftUrl={draftUrl}
      />
    </>
  );
};
